import * as msal from '@azure/msal-browser';
import {
  POPUP_AUTH_ERRORS_MESSAGES,
  type PopupAuthErrors,
} from '@/constants/auth';
import { PATH_URL } from '@/constants/path-constants';
import type { ScriptLoadState } from '@/shared/atoms';

export const MicrosoftScopes = {
  profile: 'https://graph.microsoft.com/User.Read',
  calendar: 'https://graph.microsoft.com/Calendars.Read',
  contacts: 'https://graph.microsoft.com/Contacts.Read',
  chat: 'https://graph.microsoft.com/Chat.Read',
  calendar_write: 'https://graph.microsoft.com/Calendars.ReadWrite',
  onlinemeeting_write: 'https://graph.microsoft.com/OnlineMeetings.ReadWrite',
  offline_access: 'offline_access',
  openid: 'openid',
} as const;

export type MicrosoftScopes =
  (typeof MicrosoftScopes)[keyof typeof MicrosoftScopes];
export type MicrosoftScopesKey = keyof typeof MicrosoftScopes;

/**
 * Note: MSAL automatically adds profile, offline_access, and openid
 * scopes if you use loginPopup, which we do in web. We add these
 * scopes here to inform our own backend that we've requested them
 * in the login popup.
 */
export const DEFAULT_MICROSOFT_AUTH_SCOPES: MicrosoftScopesKey[] = [
  'profile',
  'offline_access',
  'openid',
  // do not require calendar for login: https://aisense.atlassian.net/browse/OTWEB-11987
];

export const MINIMAL_MICROSOFT_CALENDAR_SCOPES: MicrosoftScopesKey[] = [
  'profile',
  'offline_access',
  'openid',
  'calendar',
];

/**
 * Note: Desktop does not use loginPopup, which means profile, offline_access,
 * and openid are not automatically added. Removing any of these 3 scopes from
 * the constant will break desktop auth.
 */
export const DESKTOP_MICROSOFT_SCOPES = 'profile offline_access openid';

/**
 * Takes scope keys and returns Microsoft scopes array which is expected
 * by Microsoft's loginPopup.
 *
 * @param scopes scope keys, e.g. 'profile', 'offline_access'
 * @returns Microsoft scopes, e.g. 'https://graph.microsoft.com/User.Read'
 */
export function getMicrosoftScopesForPopup(
  scopes: MicrosoftScopesKey[],
): MicrosoftScopes[] {
  return scopes.map((scope) => MicrosoftScopes[scope]);
}
export class FailedMicrosoftAuth extends Error {
  name = 'FailedMicrosoftAuth';
  constructor(
    public popupAuthError: PopupAuthErrors,
    public cause: unknown,
  ) {
    super(POPUP_AUTH_ERRORS_MESSAGES[popupAuthError]);
  }
}

export class MissingMicrosoftOAuthKeyError extends Error {
  constructor() {
    super('Missing Microsoft OAuth key. Check the environment variables.');
  }
}

export class MissingPublicClientApplicationInstanceError extends Error {
  name = 'MissingPublicClientApplicationInstanceError';
  message = 'Was expecting a Microsoft PublicClientApplication instance.';
}

export async function getCurrentMicrosoftObject(
  microsoftObject: msal.PublicClientApplication | null,
  setMicrosoftObject: (microsoftObject: msal.PublicClientApplication) => void,
  setScriptLoadState: (
    update: ScriptLoadState | ((prev: ScriptLoadState) => ScriptLoadState),
  ) => void,
): Promise<msal.PublicClientApplication> {
  if (!process.env.NEXT_PUBLIC_MICROSOFT_OAUTH_KEY) {
    throw new MissingMicrosoftOAuthKeyError();
  }

  if (microsoftObject) {
    return microsoftObject;
  }

  const msalConfig = {
    auth: {
      clientId: process.env.NEXT_PUBLIC_MICROSOFT_OAUTH_KEY,
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: `${location.origin}/${PATH_URL.SIGNIN}`,
    },
    cache: {
      storeAuthStateInCookie: true,
      cacheLocation: 'localStorage',
    },
  };

  const newMicrosoftObj = new msal.PublicClientApplication(msalConfig);
  await newMicrosoftObj.initialize();

  setMicrosoftObject(newMicrosoftObj);
  setScriptLoadState((prev) => ({ ...prev, microsoft: true }));

  return newMicrosoftObj;
}
